/**
 * BDL-2808 AC3 fallback: since Tailwind v3's PostCSS/JIT integration has
 * no independent watcher (see tailwindBuildFreshness.ts for the full
 * mechanism), this repo cannot guarantee the dev server always regenerates
 * CSS for a class the moment it's written. What it CAN do is make a stale
 * build LOUD instead of silent -- this installs a `window`-callable helper
 * that runs the discriminating two-class control CLAUDE.md already
 * documents by hand, and reports the result via `console.error`/`console
 * .info` so a QA sweep (agent-browser, Playwright, or a human in devtools)
 * has a mechanized check instead of eyeballing the page.
 */
import AppConfig from 'shared/config-public';

import {
	checkTailwindBuildFreshness,
	TailwindBuildFreshnessResult,
} from './tailwindBuildFreshness';

function collectServedCssText(): string {
	const chunks: string[] = [];

	Array.from(document.styleSheets).forEach((sheet) => {
		// Typed off `sheet.cssRules` itself (rather than naming the DOM
		// `CSSRuleList` type directly) so this doesn't need the DOM lib's
		// global type names in scope.
		let rules: typeof sheet.cssRules | undefined;
		try {
			rules = sheet.cssRules;
		} catch (err) {
			// Cross-origin stylesheets throw a SecurityError on cssRules
			// access -- irrelevant here (Tailwind's output is same-origin)
			// but skip rather than let one bad sheet abort the whole scan.
			return;
		}
		if (!rules) return;
		Array.from(rules).forEach((rule) => chunks.push(rule.cssText));
	});

	return chunks.join('\n');
}

/**
 * Assert that `candidateClass` is present in the CURRENTLY SERVED
 * stylesheet, using `knownStableClass` as a control (a class you're
 * certain was already generated, e.g. one used elsewhere in the app since
 * before the dev server booted). Always logs its verdict loudly -- never
 * silently returns a "looks fine" answer for the caller to ignore.
 */
export function assertTailwindFresh(
	candidateClass: string,
	knownStableClass: string,
): TailwindBuildFreshnessResult {
	const result = checkTailwindBuildFreshness({
		cssText: collectServedCssText(),
		candidateClass,
		knownStableClass,
	});

	if (!result.controlValid) {
		// eslint-disable-next-line no-console
		console.error(
			`[tailwind-freshness] INCONCLUSIVE: ${result.reason}`,
		);
	} else if (!result.fresh) {
		// eslint-disable-next-line no-console
		console.error(
			`[tailwind-freshness] STALE BUILD: ${result.reason}`,
		);
	} else {
		// eslint-disable-next-line no-console
		console.info(`[tailwind-freshness] fresh: ${result.reason}`);
	}

	return result;
}

/**
 * Exposes `window.assertTailwindFresh(candidateClass, knownStableClass)`
 * everywhere except the prod bundle -- this is a diagnostic tool for
 * whoever is actively debugging a dev/worktree server, not something real
 * users' sessions need to carry.
 */
export function installTailwindFreshnessDevHelper(): void {
	if (AppConfig.buildEnv === 'prod') return;
	if (typeof window === 'undefined') return;

	(
		window as unknown as {
			assertTailwindFresh: typeof assertTailwindFresh;
		}
	).assertTailwindFresh = assertTailwindFresh;
}
